import { Suspense, useEffect } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";
import { graphql, VariablesOf } from "relay-runtime";

import { confluenceTemplateDetailsRelayPageQuery } from "@/__generated__/confluenceTemplateDetailsRelayPageQuery.graphql";
import { LeftArrowIcon } from "@/components/confluence/icons/left-arrow-icon";
import { ConfluenceTemplateMessages } from "@/components/confluence/templates/template-messages";
import { ConfluenceTemplateAnalytics } from "@/components/confluence/templates/util/confluence-templates-analytics";
import ContentNotFoundErrorBoundary from "@/content/utils/content-not-found-error-boundary";
import { expectContent } from "@/content/utils/expectContent";
import { useLocalizedHref } from "@/libraries/web-framework";
import { withFeatureExposed } from "@/utils/feature-exposed";

import { SharedHeaderFooterContainer } from "../sections/shared-header-footer-container";
import {
    useConfluenceTemplateExperimentContext,
    ConfluenceTemplateExperimentContextType
} from "../sections/shared-header-footer-container.tsx";

import { parseQueryLocale } from "./hooks/use-query-locale";
import { TemplateCategorySection } from "./sections/template-category-section-relay";
import { TemplateDetailsSection } from "./sections/template-details-section-relay";
import { TemplateHeaderSection } from "./sections/template-header-section-relay";
import { TemplateKeyFeaturesSection } from "./sections/template-key-features-section-relay";

export type ConfluenceTemplateDetailsRelayPageTemplateProps = {
    queryRef: PreloadedQuery<confluenceTemplateDetailsRelayPageQuery>;
};

const detailPageStyles = css({
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center",
    paddingInlineStart: "16px",
    paddingInlineEnd: "16px"
});

const detailPageContentStyles = css({
    "display": "flex",
    "gap": "31px",
    "justifyContent": "center",
    "maxWidth": "100%",
    "@media(max-width: 768px)": {
        flexDirection: "column"
    }
});

const backLinkStyles = css({
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#6B778C",
    fontSize: "16px",
    fontWeight: "500",
    letterSpacing: "-0.20000000298023224px",
    maxWidth: "1166px",
    padding: "56px 0px 56px 0px",
    width: "100%"
});

const leftSectionStyles = css({
    "display": "flex",
    "flexDirection": "column",
    "width": "368px",
    "paddingBlockStart": "68px",
    "marginBlockStart": "-68px",
    "height": "100%",
    "insetBlockStart": 0,
    "@media(max-width: 768px)": {
        height: "auto",
        paddingInlineStart: "16px",
        paddingInlineEnd: "16px",
        position: "relative",
        width: "100%"
    },
    "@media(min-width: 769px)": {
        position: "sticky"
    }
});

const rightSectionStyles = css({
    "display": "flex",
    "flexDirection": "column",
    "paddingTop": "136px",
    "width": "767px",
    "@media(max-width: 768px)": {
        paddingInlineStart: "16px",
        paddingInlineEnd: "16px",
        paddingBlockStart: "0",
        width: "100%"
    }
});

const parseTemplateUrlSlug = (pathname: string) => {
    const [urlSlug, maybeTemplatesSlug] = pathname.split("/").reverse();
    return urlSlug && maybeTemplatesSlug === "templates" ? urlSlug : "";
};

export const ConfluenceTemplateDetailsRelayPageQuery = graphql`
    query confluenceTemplateDetailsRelayPageQuery(
        $locale: String!
        $urlSlug: String!
    ) {
        templates(
            search: {
                queries: {
                    first: 1
                    withLocales: [$locale]
                    where: {
                        searchText: {
                            exactlyMatching: {
                                fieldNamed: "urlSlug"
                                havingValues: [$urlSlug]
                            }
                        }
                    }
                }
            }
        ) {
            edges {
                node {
                    templateId
                    ...templateHeaderSectionRelayFragment
                    ...templateKeyFeaturesSectionRelayFragment
                    ...templateCategorySectionRelayFragment
                    ...templateDetailsSectionRelayFragment
                    name
                    pluginModuleKey
                }
            }
        }
    }
`;

export const getConfluenceTemplateDetailsRelayPageQueryVars = (
    location: Location,
    partialVars?: Partial<VariablesOf<confluenceTemplateDetailsRelayPageQuery>>
): VariablesOf<confluenceTemplateDetailsRelayPageQuery> => {
    const locale = partialVars?.locale ?? parseQueryLocale(location);

    const urlSlug =
        partialVars?.urlSlug ?? parseTemplateUrlSlug(location.pathname);

    return { locale, urlSlug };
};

type DetailPageContentRelayProps = {
    queryRef: PreloadedQuery<confluenceTemplateDetailsRelayPageQuery>;
};

const DetailPageContentRelay = ({ queryRef }: DetailPageContentRelayProps) => {
    const { templates } =
        usePreloadedQuery<confluenceTemplateDetailsRelayPageQuery>(
            ConfluenceTemplateDetailsRelayPageQuery,
            queryRef
        );

    const template = expectContent(templates.edges[0]?.node);
    const { setTemplate } = useConfluenceTemplateExperimentContext();

    const intl = useIntl();
    const templatesLink = useLocalizedHref("/software/confluence/templates");

    const linkText = intl.formatMessage({
        id: "ConfluenceTemplateMessages.backLink.cfind.1508",
        defaultMessage: "Explore all Confluence templates"
    });

    const backAriaLabel =
        ConfluenceTemplateMessages.searchBackAriaLabel.defaultMessage;

    useEffect(() => {
        if (template) {
            const templateName = intl.formatMessage(
                ConfluenceTemplateMessages.templateTitle,
                {
                    templateName: template.name ?? ""
                }
            );
            setTemplate({
                templateNameExternal: templateName,
                pluginModuleKey: template?.pluginModuleKey
            } as ConfluenceTemplateExperimentContextType);
        }
    }, [template, setTemplate]);

    return (
        <div data-testid="confluence-template-details-page">
            <div css={detailPageStyles}>
                <div css={detailPageContentStyles}>
                    <div css={leftSectionStyles}>
                        <div css={backLinkStyles}>
                            <a
                                href={templatesLink}
                                aria-label={backAriaLabel}
                                {...ConfluenceTemplateAnalytics.searchBackButton()}
                            >
                                <LeftArrowIcon size="13" />
                                {linkText}
                            </a>
                        </div>
                        <TemplateHeaderSection template={template} />
                        <TemplateKeyFeaturesSection template={template} />
                        <TemplateCategorySection template={template} />
                    </div>
                    <div
                        css={rightSectionStyles}
                        data-testid="TemplateDetailsBody-top"
                    >
                        <TemplateDetailsSection template={template} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export const ConfluenceTemplateDetailsRelayPageTemplateComponent = ({
    queryRef
}: ConfluenceTemplateDetailsRelayPageTemplateProps) => {
    return (
        <SharedHeaderFooterContainer>
            <ContentNotFoundErrorBoundary>
                <Suspense>
                    <DetailPageContentRelay queryRef={queryRef} />
                </Suspense>
            </ContentNotFoundErrorBoundary>
        </SharedHeaderFooterContainer>
    );
};

export const ConfluenceTemplateDetailsRelayPageTemplate = withFeatureExposed(
    "template-details-page-template-details-seo-v2",
    "enrolled",
    ConfluenceTemplateDetailsRelayPageTemplateComponent
);
